import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import AnimatedSection from './AnimatedSection';
import { Code, Cloud, Smartphone, Brain, Rocket, CircuitBoard } from 'lucide-react';

const Services = () => {
  const services = [
    {
      id: 'Enterprise Software Development',
      icon: Code,
      title: 'Enterprise Software Development',
      description: 'Tailored software solutions built with cutting-edge technologies to meet your unique business requirements.',
      features: ['Web Applications', 'Desktop Software', 'API Development', 'System Integration']
    },
    {
      id: 'cloud-solutions',
      icon: Cloud,
      title: 'Cloud Solutions',
      description: 'Scalable cloud infrastructure and migration services to optimize performance and reduce costs.',
      features: ['AWS/Azure Migration', 'Cloud Architecture', 'DevOps Implementation', 'Serverless Solutions']
    },
    {
      id: 'Web & Mobile App Development',
      icon: Smartphone,
      title: 'Web & Mobile App Development',
      description: 'Modern, responsive, and secure websites and innovative mobile applications for all platforms.',
      features: [
        'Responsive Web Apps (React, Next.js)',
        'iOS & Android Native Development',
        'Cross-Platform Apps (React Native, Flutter)',
        'API Integration & Deployment'
      ]
    },
    {
      id: 'Agentic AI & Gen AI',
      icon: Brain,
      title: 'Agentic AI & Gen AI',
      description: 'Advanced AI solutions leveraging latest technologies in generative and agentic artificial intelligence.',
      features: [
        'Autonomous AI Agents & Workflows',
        'Generative AI Solutions (Text, Image, Code)',
        'Conversational AI & Chatbots',
        'Predictive & Prescriptive Analytics'
      ]
    },
    {
      id: 'Product Development',
      icon: Rocket, // You might consider changing this icon to something like "Box", "Layers", or "Rocket" to better represent product development.
      title: 'Product Development',
      description: 'End-to-end product development services—from concept to launch—focused on delivering scalable, user-centric digital solutions.',
      features: [
        'Product Strategy & Roadmapping',
        'MVP Design & Development',
        'Agile Engineering & Iteration',
        'UI/UX Design',
        'Scalability & Performance Optimization'
      ]
    },
    {
      id: 'IoT Solutions',
      icon: CircuitBoard, // Consider updating to something like 'Chip', 'Radar', or 'Antenna'
      title: 'IoT Solutions',
      description: 'Build intelligent, connected ecosystems using IoT to drive operational efficiency and real-time insights.',
      features: [
        'Sensor & Device Integration',
        'Edge & Cloud Connectivity',
        'Real-Time Data Monitoring',
        'Predictive Maintenance',
        'Smart Automation'
      ]
    }    
  ];

  return (
    <section id="services" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <AnimatedSection className="text-center mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-4xl font-bold text-gray-900 mb-4"
          >
            Our <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Services</span>
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            We offer comprehensive software solutions designed to accelerate your business growth and digital transformation journey.
          </motion.p>
        </AnimatedSection>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -8, scale: 1.02 }}
              className="group bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl hover:border-blue-200 transition-all duration-300 cursor-pointer"
            >
              <Link to={`/services/${service.id}`} className="block">
                <div className="mb-6">
                  <motion.div 
                    whileHover={{ rotate: 360, scale: 1.1 }}
                    transition={{ duration: 0.5 }}
                    className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl mb-4"
                  >
                    <service.icon className="w-8 h-8 text-white" />
                  </motion.div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                </div>

                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, featureIndex) => (
                    <motion.li 
                      key={featureIndex} 
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: (index * 0.1) + (featureIndex * 0.05) }}
                      className="flex items-center text-sm text-gray-600"
                    >
                      <motion.div 
                        initial={{ scale: 0 }}
                        whileInView={{ scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: (index * 0.1) + (featureIndex * 0.05) + 0.2, type: "spring" }}
                        className="w-1.5 h-1.5 bg-blue-600 rounded-full mr-3"
                      />
                      {feature}
                    </motion.li>
                  ))}
                </ul>

                <div className="pt-6 border-t border-gray-100">
                  <motion.span
                    whileHover={{ x: 5 }}
                    className="text-blue-600 font-medium hover:text-purple-600 transition-colors duration-200 inline-flex items-center"
                  >
                    Learn More →
                  </motion.span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;